import React, { useState, useEffect } from 'react';
import { getSurveyData, getLegalSurveyData } from './dynamodb';

function SurveyReports() {
  const [surveyData, setSurveyData] = useState([]);
  const [issueData, setIssueData] = useState([]);
  const [legalData, setLegalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [general, legal] = await Promise.all([
          getSurveyData(),
          getLegalSurveyData()
        ]);
        setSurveyData(general.surveys || []);
        setIssueData(general.issues || []);
        setLegalData(legal || []);
      } catch (error) {
        console.error('Error loading survey data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // ── General survey stats ────────────────────────────────────────────────
  const getSatisfactionStats = () => {
    const stats = { 'very-satisfied': 0, 'satisfied': 0, 'neutral': 0, 'dissatisfied': 0 };
    surveyData.forEach(s => { if (s.satisfaction) stats[s.satisfaction]++; });
    return stats;
  };

  const getContinueStats = () => {
    const stats = { 'yes': 0, 'no': 0, 'unsure': 0 };
    surveyData.forEach(s => { if (s.continueFirstPort) stats[s.continueFirstPort]++; });
    return stats;
  };

  const getRightToManageStats = () => {
    const stats = { 'yes': 0, 'no': 0, 'unsure': 0 };
    surveyData.forEach(s => { if (s.rightToManage) stats[s.rightToManage]++; });
    return stats;
  };

  const getIssueTypeStats = () => {
    const stats = { 'maintenance': 0, 'charges': 0, 'communication': 0, 'staff': 0, 'other': 0 };
    issueData.forEach(i => { if (i.issueType) stats[i.issueType]++; });
    return stats;
  };

  // ── Legal survey stats ──────────────────────────────────────────────────
  const getSupportLeavingStats = () => {
    const stats = { 'yes': 0, 'no': 0, 'unsure': 0 };
    legalData.forEach(s => { if (s.supportLeaving) stats[s.supportLeaving]++; });
    return stats;
  };

  const getSupportLegalRepStats = () => {
    const stats = { 'yes': 0, 'no': 0, 'unsure': 0 };
    legalData.forEach(s => { if (s.supportLegalRep) stats[s.supportLegalRep]++; });
    return stats;
  };

  const getWillingToPayStats = () => {
    const stats = { 'yes': 0, 'no': 0, 'unsure': 0 };
    legalData.forEach(s => { if (s.willingToPay) stats[s.willingToPay]++; });
    return stats;
  };

  // ── Shared bar chart renderer ───────────────────────────────────────────
  const renderBarChart = (data, title, colors, labelMap = {}) => {
    const total = Object.values(data).reduce((sum, val) => sum + val, 0);
    const maxValue = Math.max(...Object.values(data), 1);

    return (
      <div className="stat-card mb-4">
        <h5 className="mb-3">{title}</h5>
        {Object.entries(data).map(([key, value], index) => (
          <div key={key} className="mb-2">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-capitalize">{labelMap[key] || key.replace(/-/g, ' ')}</span>
              <span>{value} ({total > 0 ? Math.round((value / total) * 100) : 0}%)</span>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar"
                style={{
                  width: `${(value / maxValue) * 100}%`,
                  backgroundColor: colors[index % colors.length]
                }}
              ></div>
            </div>
          </div>
        ))}
        <small className="text-muted">Total responses: {total}</small>
      </div>
    );
  };

  const satisfactionStats    = getSatisfactionStats();
  const continueStats        = getContinueStats();
  const rightToManageStats   = getRightToManageStats();
  const issueTypeStats       = getIssueTypeStats();
  const supportLeavingStats  = getSupportLeavingStats();
  const supportLegalRepStats = getSupportLegalRepStats();
  const willingToPayStats    = getWillingToPayStats();

  const yesNoColors  = ['#10b981', '#ef4444', '#6b7280'];
  const yesNoLabels  = { yes: 'Yes', no: 'No', unsure: 'Unsure' };

  if (isLoading) {
    return (
      <div className="survey-container">
        <div className="survey-pattern"></div>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="modern-form p-5 fade-in text-center">
                <div className="survey-icon mx-auto">📊</div>
                <h1 className="survey-title">Survey Reports</h1>
                <p className="survey-subtitle">Loading data from database...</p>
                <div className="spinner-border text-primary mt-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container">
      <div className="survey-pattern"></div>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="modern-form p-5 fade-in">

              {/* ── Header ── */}
              <div className="text-center mb-5">
                <div className="survey-icon mx-auto">📊</div>
                <h1 className="survey-title">Survey Reports</h1>
                <p className="survey-subtitle">Analysis of resident feedback and issue reports</p>
              </div>

              {/* ── Summary counters ── */}
              <div className="row mb-5">
                <div className="col-md-3">
                  <div className="stat-card text-center">
                    <div className="stat-number">{surveyData.length}</div>
                    <div className="stat-label">General Surveys</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card text-center">
                    <div className="stat-number">{issueData.length}</div>
                    <div className="stat-label">Issues Reported</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card text-center">
                    <div className="stat-number">{legalData.length}</div>
                    <div className="stat-label">Legal Survey Responses</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card text-center">
                    <div className="stat-number">
                      {rightToManageStats.yes > 0 ? Math.round((rightToManageStats.yes / 107) * 100) : 0}%
                    </div>
                    <div className="stat-label">Support Right to Manage</div>
                    <div className="text-muted small">{rightToManageStats.yes} of 107 houses</div>
                  </div>
                </div>
              </div>

              {/* ── General survey section ── */}
              <h4 className="mb-4 pb-2" style={{ borderBottom: '2px solid #e2e8f0', color: '#1e293b' }}>
                General Survey
              </h4>
              <div className="row">
                <div className="col-md-6">
                  {renderBarChart(
                    satisfactionStats,
                    'FirstPort Satisfaction Levels',
                    ['#10b981', '#3b82f6', '#f59e0b', '#ef4444']
                  )}
                </div>
                <div className="col-md-6">
                  {renderBarChart(
                    continueStats,
                    'Continue Using FirstPort?',
                    yesNoColors,
                    yesNoLabels
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  {renderBarChart(
                    rightToManageStats,
                    'Exercise Right to Manage?',
                    yesNoColors,
                    yesNoLabels
                  )}
                </div>
                <div className="col-md-6">
                  {renderBarChart(
                    issueTypeStats,
                    'Issue Types Reported',
                    ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#6b7280']
                  )}
                </div>
              </div>

              {/* ── Legal survey section ── */}
              <h4 className="mt-5 mb-4 pb-2" style={{ borderBottom: '2px solid #e2e8f0', color: '#1e293b' }}>
                Legal Action Survey
              </h4>

              {legalData.length === 0 ? (
                <p className="text-muted">No legal survey responses yet.</p>
              ) : (
                <>
                  {/* Key stat — support for leaving */}
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <div className="stat-card text-center">
                        <div className="stat-number" style={{ color: '#10b981' }}>
                          {supportLeavingStats.yes > 0
                            ? Math.round((supportLeavingStats.yes / legalData.length) * 100)
                            : 0}%
                        </div>
                        <div className="stat-label">Support Leaving FirstPort</div>
                        <div className="text-muted small">{supportLeavingStats.yes} of {legalData.length} responses</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stat-card text-center">
                        <div className="stat-number" style={{ color: '#2563eb' }}>
                          {supportLegalRepStats.yes > 0
                            ? Math.round((supportLegalRepStats.yes / legalData.length) * 100)
                            : 0}%
                        </div>
                        <div className="stat-label">Support Legal Representation</div>
                        <div className="text-muted small">{supportLegalRepStats.yes} of {legalData.length} responses</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stat-card text-center">
                        <div className="stat-number" style={{ color: '#f59e0b' }}>
                          {willingToPayStats.yes > 0
                            ? Math.round((willingToPayStats.yes / legalData.length) * 100)
                            : 0}%
                        </div>
                        <div className="stat-label">Willing to Contribute to Costs</div>
                        <div className="text-muted small">{willingToPayStats.yes} of {legalData.length} responses</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      {renderBarChart(
                        supportLeavingStats,
                        'Support Leaving FirstPort?',
                        yesNoColors,
                        yesNoLabels
                      )}
                    </div>
                    <div className="col-md-4">
                      {renderBarChart(
                        supportLegalRepStats,
                        'Support Legal Representation?',
                        yesNoColors,
                        yesNoLabels
                      )}
                    </div>
                    <div className="col-md-4">
                      {renderBarChart(
                        willingToPayStats,
                        'Willing to Contribute to Costs?',
                        yesNoColors,
                        yesNoLabels
                      )}
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SurveyReports;
